import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSupplierAuth } from '../../context/SupplierAuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  LayoutDashboard, 
  Settings, 
  LogOut,
  Menu,
  X,
  Inbox,
  FileText,
  Building2,
  Shield,
  Users,
  CreditCard,
  Moon,
  Sun,
  CheckCircle,
  AlertCircle,
  Star
} from 'lucide-react';
import { Button } from '../ui/button';

const SPOOKER_LOGO = "https://spooker.io/wp-content/uploads/2022/12/logo.svg";

const SupplierLayout = ({ children }) => {
  const { user, organization, logout } = useSupplierAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const mainNavigation = [
    { name: 'Dashboard', href: '/leverancier', icon: LayoutDashboard },
    { name: 'Lead Inbox', href: '/leverancier/leads', icon: Inbox },
    { name: 'Beoordelingen', href: '/leverancier/ratings', icon: Star },
    { name: 'Profiel Wizard', href: '/leverancier/profiel', icon: Building2 },
    { name: 'Financiën', href: '/leverancier/finance', icon: CreditCard },
  ];

  const beheerNavigation = [
    { name: 'Gebruikers', href: '/leverancier/users', icon: Users },
    { name: 'Compliance', href: '/leverancier/compliance', icon: Shield },
    { name: 'Instellingen', href: '/leverancier/settings', icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate('/leverancier/login');
  };

  const isActive = (href) => {
    if (href === '/leverancier') {
      return location.pathname === '/leverancier';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className={`min-h-screen bg-white dark:bg-neutral-950 transition-colors`}>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/20 dark:bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800 transform transition-transform duration-200 ease-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-neutral-200 dark:border-neutral-800">
            <Link to="/leverancier" className="flex items-center gap-2">
              <img src={SPOOKER_LOGO} alt="Spooker" className="h-6 dark:invert" />
              <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                Leverancier
              </span>
            </Link>
            <button
              className="lg:hidden p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5 dark:text-white" strokeWidth={1.5} />
            </button>
          </div>

          {/* Organization Status Badge with Logo */}
          {organization && (
            <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center gap-3">
                {/* Company Logo */}
                <div className="h-10 w-10 rounded-lg border border-neutral-200 dark:border-neutral-700 flex items-center justify-center bg-neutral-50 dark:bg-neutral-800 overflow-hidden flex-shrink-0">
                  {organization.logo_url ? (
                    <img 
                      src={organization.logo_url} 
                      alt={organization.company_name} 
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <Building2 className="h-5 w-5 text-neutral-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {organization.status === 'approved' ? (
                      <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300 truncate">
                      {organization.company_name}
                    </span>
                  </div>
                  <span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${
                    organization.status === 'approved' 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : organization.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    {organization.status === 'approved' ? 'Actief' : 
                     organization.status === 'pending' ? 'In behandeling' : 
                     'Geblokkeerd'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            {/* Main Navigation */}
            <div className="px-3 mb-6">
              <p className="px-3 mb-2 text-xs font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                Overzicht
              </p>
              <ul className="space-y-1">
                {mainNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          active
                            ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-white'
                            : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 hover:text-neutral-900 dark:hover:text-white'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.5} />
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Beheer Navigation */}
            <div className="px-3">
              <p className="px-3 mb-2 text-xs font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                Beheer
              </p>
              <ul className="space-y-1">
                {beheerNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          active
                            ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-white'
                            : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 hover:text-neutral-900 dark:hover:text-white'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.5} />
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-neutral-200 dark:border-neutral-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center">
                <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                  {user?.full_name?.charAt(0) || 'L'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                  {user?.full_name || 'Leverancier'}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Uitloggen
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 dark:bg-neutral-950/80 backdrop-blur-md border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5 dark:text-white" strokeWidth={1.5} />
              </button>
              <div className="lg:hidden">
                <img src={SPOOKER_LOGO} alt="Spooker" className="h-5 dark:invert" />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-neutral-600 dark:text-neutral-400"
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default SupplierLayout;
